import React, { useEffect, useMemo, useState } from "react";
import { FiX, FiSettings } from "react-icons/fi";
import {
    useGetPdfSettingsQuery,
    useUpdatePdfSettingsMutation,
    useExportTestPdfMutation,
  } from "../context/apiSlice";
  


const FONT_SIZES = [
  { value: "small", label: "Kichik" },
  { value: "medium", label: "O‘rta" },
  { value: "large", label: "Katta" },
];

const PAGE_FORMATS = ["A4", "Letter", "Legal"];
const ORIENTATIONS = [
  { value: "portrait", label: "Tik" },
  { value: "landscape", label: "Yotiq" },
];

const NUMBERING = [
  { value: "numeric", label: "Raqamli (1, 2, 3...)" },
  { value: "none", label: "Raqamsiz" },
];

function Toggle({ value, onChange }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!value)}
      className={`w-12 h-7 rounded-full border transition relative ${
        value ? "bg-black border-black" : "bg-gray-200 border-gray-200"
      }`}
      aria-label="toggle"
    >
      <span
        className={`absolute top-0.5 w-6 h-6 rounded-full bg-white transition ${
          value ? "left-5" : "left-0.5"
        }`}
      />
    </button>
  );
}

export default function PdfSettingsModal({ open, onClose, testId }) {
  const skip = !open || !testId;
  const [exportPdf, { isLoading: isExporting }] = useExportTestPdfMutation();

  const { data: serverSettings, isFetching } = useGetPdfSettingsQuery(testId, {
    skip,
    refetchOnMountOrArgChange: true,
  });

  const [updatePdfSettings, { isLoading: isSaving }] =
    useUpdatePdfSettingsMutation();


  const defaults = useMemo(
    () => ({
      showCorrectAnswers: false,
      fontSize: "medium",
      pageFormat: "Letter",
      orientation: "portrait",
      includeDescription: false,
      questionNumbering: "numeric",
      showOptionLabels: true,
      showCheckboxes: false,
    }),
    []
  );

  const [form, setForm] = useState(defaults);
  async function handleExportPdf() {
    try {
      const blob = await exportPdf({ testId }).unwrap();
  
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `test-${testId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("PDF eksportda xato");
    }
  }
  

  useEffect(() => {
    if (serverSettings) setForm({ ...defaults, ...serverSettings });
  }, [serverSettings, defaults]);

  if (!open) return null;

  async function handleSave() {
    try {
      await updatePdfSettings({ testId, settings: form }).unwrap();
      onClose?.();
    } catch (e) {
      alert(e?.data?.message || e?.error || "Saqlashda xato");
    }
  }


 
  
  

  // ⚠️ Agar token LocalStorage’da bo‘lsa (Bearer), yuqoridagi fetchga header qo‘sh:
  // Authorization: `Bearer ${localStorage.getItem("token")}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* overlay */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      {/* modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <FiSettings className="w-4 h-4" />
            <h3 className="text-sm font-semibold">PDF eksport sozlamalari</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-xl hover:bg-gray-50 flex items-center justify-center"
          >
            <FiX />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {isFetching && (
            <p className="text-xs text-gray-400">Yuklanmoqda...</p>
          )}

          <div className="rounded-xl border p-4 space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">To‘g‘ri javoblarni qo‘shish</p>
              <Toggle
                value={form.showCorrectAnswers}
                onChange={(v) => setForm((p) => ({ ...p, showCorrectAnswers: v }))}
              />
            </div>
            <p className="text-xs text-gray-500">
              Eksport qilingan PDF da to‘g‘ri javoblarni ko‘rsatish
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-xs text-gray-500">Shrift o‘lchami</p>
              <select
                className="w-full border rounded-xl px-3 py-2 text-sm"
                value={form.fontSize}
                onChange={(e) => setForm((p) => ({ ...p, fontSize: e.target.value }))}
              >
                {FONT_SIZES.map((x) => (
                  <option key={x.value} value={x.value}>{x.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <p className="text-xs text-gray-500">Sahifa formati</p>
              <select
                className="w-full border rounded-xl px-3 py-2 text-sm"
                value={form.pageFormat}
                onChange={(e) => setForm((p) => ({ ...p, pageFormat: e.target.value }))}
              >
                {PAGE_FORMATS.map((x) => (
                  <option key={x} value={x}>{x}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1 col-span-2">
              <p className="text-xs text-gray-500">Yo‘nalish</p>
              <select
                className="w-full border rounded-xl px-3 py-2 text-sm"
                value={form.orientation}
                onChange={(e) => setForm((p) => ({ ...p, orientation: e.target.value }))}
              >
                {ORIENTATIONS.map((x) => (
                  <option key={x.value} value={x.value}>{x.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="rounded-xl border p-4 space-y-1">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Tavsifni qo‘shish</p>
              <Toggle
                value={form.includeDescription}
                onChange={(v) => setForm((p) => ({ ...p, includeDescription: v }))}
              />
            </div>
            <p className="text-xs text-gray-500">
              PDF faylga dars tavsifini (summary) qo‘shish
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-gray-500">Savol raqamlash uslubi</p>
            <select
              className="w-full border rounded-xl px-3 py-2 text-sm"
              value={form.questionNumbering}
              onChange={(e) => setForm((p) => ({ ...p, questionNumbering: e.target.value }))}
            >
              {NUMBERING.map((x) => (
                <option key={x.value} value={x.value}>{x.label}</option>
              ))}
            </select>
          </div>

          <div className="rounded-xl border p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Javob belgilarini ko‘rsatish</p>
                <p className="text-xs text-gray-500">A), B), C), D) ko‘rsatish</p>
              </div>
              <Toggle
                value={form.showOptionLabels}
                onChange={(v) => setForm((p) => ({ ...p, showOptionLabels: v }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Belgi qutichalarini ko‘rsatish</p>
                <p className="text-xs text-gray-500">Variantlar oldida [ ]</p>
              </div>
              <Toggle
                value={form.showCheckboxes}
                onChange={(v) => setForm((p) => ({ ...p, showCheckboxes: v }))}
              />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl border text-sm"
            type="button"
          >
            Bekor qilish
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 rounded-xl bg-black text-white text-sm disabled:opacity-60"
            type="button"
          >
            {isSaving ? "Saqlanmoqda..." : "Sozlamalarni saqlash"}
          </button>

          <button
  onClick={handleExportPdf}
  disabled={isExporting}
  className="px-4 py-2 rounded-xl bg-gray-900 text-white text-sm disabled:opacity-60"
  type="button"
>
  {isExporting ? "Yuklanmoqda..." : "PDF eksport"}
</button>


        </div>
      </div>
    </div>
  );
}
